// Dietary suitability is an intrinsic property of a recipe (determined by its
// ingredients), so it lives on the recipe itself — not in the user's categories.
// Stored on Recipe.dietary as an array of these tags.
export enum DietaryTag {
  Vegetarian = "vegetarian",
  Vegan = "vegan",
  GlutenFree = "gluten-free",
  DairyFree = "dairy-free",
  EggFree = "egg-free",
  NutFree = "nut-free",
  Keto = "keto",
}

// Canonical order + display labels, used by the recipe form and the filter bar.
// `label` is the English fallback; screens should render `t(DIETARY_LABEL_KEYS[tag])`
// so the label localises while the stored tag stays the fixed identifier.
export const DIETARY_TAGS: { tag: DietaryTag; label: string }[] = [
  { tag: DietaryTag.Vegetarian, label: "Vegetarian" },
  { tag: DietaryTag.Vegan, label: "Vegan" },
  { tag: DietaryTag.GlutenFree, label: "Gluten-Free" },
  { tag: DietaryTag.DairyFree, label: "Dairy-Free" },
  { tag: DietaryTag.EggFree, label: "Egg-Free" },
  { tag: DietaryTag.NutFree, label: "Nut-Free" },
  { tag: DietaryTag.Keto, label: "Keto" },
];

// Dietary tag value → i18n key (display only; the stored tag is the identifier).
export const DIETARY_LABEL_KEYS: Record<DietaryTag, string> = {
  [DietaryTag.Vegetarian]: "labels.dietary.vegetarian",
  [DietaryTag.Vegan]: "labels.dietary.vegan",
  [DietaryTag.GlutenFree]: "labels.dietary.glutenFree",
  [DietaryTag.DairyFree]: "labels.dietary.dairyFree",
  [DietaryTag.EggFree]: "labels.dietary.eggFree",
  [DietaryTag.NutFree]: "labels.dietary.nutFree",
  [DietaryTag.Keto]: "labels.dietary.keto",
};

// Vegan implies vegetarian. Keep the stored set consistent and in canonical
// order so filtering is a plain `includes()` and the UI is predictable.
export function normalizeDietary(tags: DietaryTag[] = []): DietaryTag[] {
  const set = new Set(tags);
  if (set.has(DietaryTag.Vegan)) set.add(DietaryTag.Vegetarian);
  return DIETARY_TAGS.map((d) => d.tag).filter((t) => set.has(t));
}
